// Play / Alignment picker (docs/02-Game-Design/Alignments.md): once the
// word that surrounds a planet has settled, a dark mask comes over the sky
// with the planet's three alignments as cards, the planet peeking from
// behind each; tapping a card lifts its planet with a light bounce, and
// Confirm Alignment sends the choice. Nothing is chosen on opening; there
// is no cancel: the choice is for the night.
import { alignmentName, alignmentText, planetAppearance, planetName, planetsOf, type AlignmentId, type PlayLevel, type PlayState } from '@wits/core';
import { useState } from 'react';
import { Button } from '../components/Button';
import { PlanetIcon } from '../sky/PlanetIcon';

interface Props {
  level: PlayLevel;
  board: PlayState;
  /** The planet waiting for its alignment. */
  planetId: number;
  busy: boolean;
  onConfirm: (alignment: AlignmentId) => void;
}

export function AlignmentPicker({ level, board, planetId, busy, onConfirm }: Props) {
  const [chosen, setChosen] = useState<AlignmentId | null>(null);
  const planet = planetsOf(level).find((p) => p.id === planetId);
  if (!planet) return null;
  const name = planetName(planetAppearance(level, planet).kind);
  const offered = planet.alignments ?? [];
  return (
    <div className="align-picker-root">
      <div className="align-picker" role="dialog" aria-modal="true" aria-labelledby="align-picker-title">
        <h2 id="align-picker-title" className="align-picker-title">
          Choose your {name} alignment
        </h2>
        <div className="align-picker-options" role="radiogroup" aria-label={`Alignments of ${name}`}>
          {offered.map((id) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={chosen === id}
              className={chosen === id ? 'align-picker-option is-chosen' : 'align-picker-option'}
              onClick={() => setChosen(id)}
            >
              <span className="align-picker-orb">
                <PlanetIcon level={level} planet={planet} size={96} />
              </span>
              <span className="align-picker-card">
                <span className="align-picker-name">{alignmentName(id)}</span>
                <span className="align-picker-sep" aria-hidden="true" />
                <span className="align-picker-rule">{alignmentText(level, board, id)}</span>
              </span>
            </button>
          ))}
        </div>
        <Button variant="primary" className="align-picker-confirm" disabled={chosen === null || busy} onClick={() => chosen && onConfirm(chosen)}>
          Confirm Alignment
        </Button>
      </div>
    </div>
  );
}
